// Phase JB.2 — Trial Card.
//
// States:
//   - anon:                       hidden (returns null)
//   - free + trial_eligible:      "Start your 7-day trial" CTA + N05 honesty copy
//   - free + ineligible:          hidden (the trial is one-time only)
//   - active trial:               daysRemaining + Countdown + "Activate Pro" CTA
//   - expired/converted/cancelled trial: hidden (flow continues elsewhere)
//
// Composes: Eyebrow + Stat + Badge + Countdown + Pill — I0 primitives only.
//
// N03 (no dark patterns) / N05 (trial honesty) / N07 (no fake urgency):
//   - Mandatory copy: "No card required. Trial reverts to free at day 8 unless you activate Pro."
//   - No countdown that does not match a real timestamp.
//   - No discount language. No "miss out" framing.

import { useEffect, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import { Badge, Countdown, Eyebrow, Pill, Stat } from "../super-acca";
import { useAuthSession } from "../../auth/useAuthSession.ts";
import { useTrial, type Trial, type TrialSource } from "../../conversion/useTrial.ts";
import { getBrowserClient } from "../../supabase/browser.ts";

const fontMono = "var(--sa-font-mono)";
const fontBody = "var(--sa-font-body)";

const wrapperStyle: CSSProperties = { padding: "12px 48px 12px" };
const cardStyle: CSSProperties = {
  border: "1px solid var(--sa-hairline)",
  background: "var(--sa-bg-panel)",
  borderRadius: 8,
  padding: 22,
  margin: "0 auto",
  maxWidth: 1180,
};

export const TRIAL_HONESTY_COPY =
  "No card required. Trial reverts to free at day 8 unless you activate Pro.";

export interface TrialCardProps {
  /** Test override: pass a profile.trial_eligible value. */
  trialEligibleOverride?: boolean | null;
  /** Test override: pass a trial row. */
  trialOverride?: Trial | null;
  /** Test override: force unauth state. */
  forceAnon?: boolean;
  /** Where this card lives — passed to startTrial. */
  source?: TrialSource;
}

export function TrialCard({
  trialEligibleOverride,
  trialOverride,
  forceAnon,
  source = "manual",
}: TrialCardProps = {}): JSX.Element | null {
  const { user } = useAuthSession();
  const { trial: liveTrial, daysRemaining, startTrial, loading, error } = useTrial();
  const trial = trialOverride === undefined ? liveTrial : trialOverride;
  const isAnon = forceAnon === true || !user;

  const [eligible, setEligible] = useState<boolean | null>(
    trialEligibleOverride === undefined ? null : trialEligibleOverride,
  );

  useEffect(() => {
    if (trialEligibleOverride !== undefined) {
      setEligible(trialEligibleOverride);
      return;
    }
    if (!user) {
      setEligible(null);
      return;
    }
    let cancelled = false;
    const sb = getBrowserClient();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (sb.from("profiles" as never) as any)
      .select("trial_eligible")
      .eq("id", user.id)
      .maybeSingle()
      .then((res: { data: { trial_eligible: boolean } | null }) => {
        if (cancelled) return;
        setEligible(res.data?.trial_eligible ?? null);
      });
    return () => {
      cancelled = true;
    };
  }, [user, trialEligibleOverride]);

  if (isAnon) return null;

  // ── Active trial branch ──
  if (trial && trial.status === "active") {
    return (
      <section aria-label="7-day Trial" data-testid="trial-card-active" style={wrapperStyle}>
        <div style={cardStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <Eyebrow>7-Day Pro Trial</Eyebrow>
            <Badge variant="amber">TRIAL ACTIVE</Badge>
          </div>
          <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
            <Stat
              label="Days remaining"
              value={daysRemaining === null ? "—" : String(daysRemaining)}
              accent
            />
            <div>
              <span
                style={{
                  fontFamily: fontMono,
                  fontSize: 10.5,
                  letterSpacing: "0.18em",
                  color: "var(--sa-fg-muted)",
                }}
              >
                EXPIRES
              </span>
              <div style={{ marginTop: 4 }}>
                <Countdown targetISO={trial.expires_at} compact />
              </div>
            </div>
          </div>
          <div
            style={{
              marginTop: 18,
              fontFamily: fontBody,
              fontSize: 13,
              color: "var(--sa-fg-secondary)",
              lineHeight: 1.6,
            }}
          >
            <p>{TRIAL_HONESTY_COPY}</p>
          </div>
          <div style={{ marginTop: 14 }}>
            <Link
              to="/pricing"
              data-testid="trial-card-activate-cta"
              style={{ textDecoration: "none" }}
            >
              <Pill color="var(--sa-accent-saffron)" borderColor="var(--sa-accent-saffron)">
                ACTIVATE PRO · PAYSTACK
              </Pill>
            </Link>
          </div>
        </div>
      </section>
    );
  }

  // ── Eligible-to-start branch ──
  if (eligible === true && (!trial || trial.status === "cancelled")) {
    return (
      <section
        aria-label="7-day Trial"
        data-testid="trial-card-eligible"
        style={wrapperStyle}
      >
        <div style={cardStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <Eyebrow>Start your 7-day trial</Eyebrow>
            <Badge variant="ghost">FREE</Badge>
          </div>
          <p
            style={{
              marginTop: 14,
              fontFamily: fontBody,
              fontSize: 14,
              color: "var(--sa-fg-secondary)",
              lineHeight: 1.7,
            }}
          >
            Seven days of full Pro intelligence — every signal, every reasoning trace,
            the complete agent vote. After the trial ends your account returns to the
            free tier automatically.
          </p>
          <p
            style={{
              marginTop: 12,
              fontFamily: fontBody,
              fontSize: 13,
              color: "var(--sa-fg-primary)",
              lineHeight: 1.7,
              fontWeight: 500,
            }}
            data-testid="trial-card-honesty"
          >
            {TRIAL_HONESTY_COPY}
          </p>
          {error !== null ? (
            <div
              style={{
                marginTop: 12,
                fontFamily: fontMono,
                fontSize: 11,
                color: "var(--sa-signal-red)",
              }}
              data-testid="trial-card-error"
            >
              {error}
            </div>
          ) : null}
          <div style={{ marginTop: 16 }}>
            <button
              type="button"
              onClick={() => void startTrial(source)}
              disabled={loading}
              data-testid="trial-card-start-cta"
              style={{ background: "transparent", border: 0, padding: 0, cursor: loading ? "default" : "pointer" }}
            >
              <Pill color="var(--sa-accent-saffron)" borderColor="var(--sa-accent-saffron)">
                {loading ? "STARTING…" : "START 7-DAY TRIAL"}
              </Pill>
            </button>
          </div>
        </div>
      </section>
    );
  }

  // ── Hide for: ineligible, expired, converted, loading-pre-fetch ──
  return null;
}
